/**
 * Hook to get current user's team member role for a celebration
 * Task 5.8: Update Permission Checks in Frontend
 */

import { useState, useEffect } from 'react';
import { teamMemberService } from '@/lib/api/teamMember.service';
import { useAuth } from './useAuth';
import type { TeamRole } from '@/types/teamMember.types';

export function useTeamMemberRole(celebrationId: number | null | undefined) {
  const { user } = useAuth();
  const [role, setRole] = useState<TeamRole | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!celebrationId || !user) {
      setRole(null);
      setLoading(false);
      return;
    }

    const fetchRole = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Get all team members and find current user
        const members = await teamMemberService.getTeamMembers(celebrationId);
        const currentMember = members.find(m => m.user_id === user.id);
        
        setRole(currentMember?.role || null);
      } catch (err: any) {
        console.error('Error fetching team member role:', err);
        setError(err.response?.data?.error || 'Failed to fetch role');
        setRole(null);
      } finally {
        setLoading(false);
      }
    };

    fetchRole();
  }, [celebrationId, user]);

  return { role, loading, error };
}
